export type ArrayMergeConfig = ReadonlyMap<string, string | undefined>;

export const isObjectAndNotArray = (item: any): item is Record<string, any> => {
    return item && typeof item === 'object' && !Array.isArray(item);
};

export const deepMerge = <T extends Record<string, any>, U extends Record<string, any>>(
    baseObj: T,
    extendingObj: U,
    arraysToMerge: ArrayMergeConfig = new Map(),
    currentPath = ''
): T & U => {
    const result: any = { ...baseObj };

    for (const key in extendingObj) {
        const extendingValue = extendingObj[key];
        const baseValue = baseObj[key];
        const path = currentPath ? `${currentPath}.${key}` : key;

        if (Array.isArray(extendingValue)) {
            if (arraysToMerge.has(path) && Array.isArray(baseValue)) {
                result[key] = mergeArrays(baseValue, extendingValue, arraysToMerge.get(path));
            } else {
                result[key] = [...extendingValue];
            }
        } else if (isObjectAndNotArray(extendingValue) && isObjectAndNotArray(baseValue)) {
            result[key] = deepMerge(baseValue, extendingValue, arraysToMerge, path);
        } else {
            result[key] = extendingValue;
        }
    }

    return result as T & U;
};

const mergeArrays = (baseArray: any[], extendingArray: any[], mergeKey?: string) => {
    if (!mergeKey) {
        return [...baseArray, ...extendingArray];
    }

    const result = [...baseArray];

    for (const extendingItem of extendingArray) {
        const existingIndex = result.findIndex(
            (item) => isObjectAndNotArray(item) && isObjectAndNotArray(extendingItem) && item[mergeKey] === extendingItem[mergeKey]
        );

        if (existingIndex !== -1) {
            result[existingIndex] = {
                ...result[existingIndex],
                ...extendingItem
            };
        } else {
            result.push(extendingItem);
        }
    }

    return result;
};
